import { useQuery } from "@tanstack/react-query"
import { useParams, useNavigate } from "react-router-dom"
import { useState, useEffect } from "react"
import { ArrowLeft, Image as ImageIcon, Pencil } from "lucide-react"
import { waterQualityApi } from "../api/waterQualityApi"
import { LocationDisplay } from "@/shared/components/ui/LocationDisplay"
import { imageApi } from "@/shared/api/imageApi"
import { queryKeys } from "@/shared/api/queryClient"
import { useFarmFromUrl, usePondFromUrl } from "@/shared/hooks/useUrlSync"
import { Card } from "@/shared/components/ui/Card"
import { Button } from "@/shared/components/ui/Button"
import { PageLoading } from "@/shared/components/ui/LoadingSpinner"
import { ImageGallery } from "@/shared/components/ui/ImageGallery"
import { useImageStore } from "@/shared/stores/imageStore"
import { formatDateTime } from "@/shared/lib/utils"
import { buildRoute } from "@/shared/lib/constants"

export function WaterQualityDetailPage() {
  const { testId } = useParams<{ testId: string }>()
  const navigate = useNavigate()
  const [showGallery, setShowGallery] = useState(false)
  const [galleryStartIndex, setGalleryStartIndex] = useState(0)

  const { farmId, isLoading: isFarmLoading } = useFarmFromUrl()
  const { pondId, pond, isLoading: isPondLoading } = usePondFromUrl(farmId)

  // Zustand image store - extract only the setImages function
  const setImages = useImageStore((state) => state.setImages)

  // Fetch water quality test details
  const { data: test, isLoading: isTestLoading } = useQuery({
    queryKey: queryKeys.waterQuality.detail(farmId || "", pondId || "", testId || ""),
    queryFn: () => waterQualityApi.get(farmId!, pondId!, testId!),
    enabled: !!farmId && !!pondId && !!testId,
  })

  // Fetch signed URLs for images
  const { data: signedUrlsResponse, isLoading: isImagesLoading } = useQuery({
    queryKey: queryKeys.images.signedUrls(test?.imageIds || []),
    queryFn: () => imageApi.getSignedUrls(test?.imageIds || []),
    enabled: !!test?.imageIds && test.imageIds.length > 0,
  })

  // Update Zustand cache when signed URLs are fetched
  useEffect(() => {
    if (signedUrlsResponse?.images) {
      setImages(signedUrlsResponse.images)
    }
  }, [signedUrlsResponse, setImages])

  const handleClose = () => {
    if (farmId && pondId) {
      navigate(buildRoute.waterQuality(farmId, pondId))
    }
  }

  const handleImageClick = (index: number) => {
    setGalleryStartIndex(index)
    setShowGallery(true)
  }

  if (isFarmLoading || isPondLoading || isTestLoading) {
    return <PageLoading message="Loading water quality test..." />
  }

  if (!test) {
    return (
      <Card>
        <div className="text-center py-16">
          <h3 className="font-roboto text-base font-semibold text-gray-900">
            Test not found
          </h3>
          <p className="font-roboto text-sm text-gray-600 mt-2">
            The water quality test you're looking for doesn't exist.
          </p>
          <Button onClick={handleClose} className="mt-4">
            Back to Tests
          </Button>
        </div>
      </Card>
    )
  }

  const images = signedUrlsResponse?.images || []

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-roboto text-2xl font-bold text-gray-900">
              Water Quality Test Details
            </h2>
            <p className="font-roboto text-sm text-gray-600 mt-1">
              {pond?.name} • {formatDateTime(test.testedAt)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              onClick={() => navigate(buildRoute.waterQualityEdit(farmId!, testId!, pondId))}
            >
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
            <Button variant="ghost" onClick={handleClose}>
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Water Quality
            </Button>
          </div>
        </div>

        {/* Test Parameters */}
        <Card>
          <h3 className="font-roboto text-lg font-semibold text-gray-900 mb-4">
            Water Parameters
          </h3>
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
            {test.dissolvedOxygen !== undefined && (
              <div>
                <p className="font-roboto text-xs text-gray-500 mb-1">Dissolved Oxygen</p>
                <p className="font-roboto text-2xl font-semibold font-mono text-gray-900">
                  {test.dissolvedOxygen}
                  <span className="text-sm font-normal text-gray-600 ml-1">mg/L</span>
                </p>
              </div>
            )}
            {test.ph !== undefined && (
              <div>
                <p className="font-roboto text-xs text-gray-500 mb-1">pH</p>
                <p className="font-roboto text-2xl font-semibold font-mono text-gray-900">
                  {test.ph}
                </p>
              </div>
            )}
            {test.temperature !== undefined && (
              <div>
                <p className="font-roboto text-xs text-gray-500 mb-1">Temperature</p>
                <p className="font-roboto text-2xl font-semibold font-mono text-gray-900">
                  {test.temperature}
                  <span className="text-sm font-normal text-gray-600 ml-1">°C</span>
                </p>
              </div>
            )}
            {test.salinity !== undefined && (
              <div>
                <p className="font-roboto text-xs text-gray-500 mb-1">Salinity</p>
                <p className="font-roboto text-2xl font-semibold font-mono text-gray-900">
                  {test.salinity}
                  <span className="text-sm font-normal text-gray-600 ml-1">ppt</span>
                </p>
              </div>
            )}
            {test.ammonia !== undefined && (
              <div>
                <p className="font-roboto text-xs text-gray-500 mb-1">Ammonia</p>
                <p className="font-roboto text-2xl font-semibold font-mono text-gray-900">
                  {test.ammonia}
                  <span className="text-sm font-normal text-gray-600 ml-1">mg/L</span>
                </p>
              </div>
            )}
            {test.nitrite !== undefined && (
              <div>
                <p className="font-roboto text-xs text-gray-500 mb-1">Nitrite</p>
                <p className="font-roboto text-2xl font-semibold font-mono text-gray-900">
                  {test.nitrite}
                  <span className="text-sm font-normal text-gray-600 ml-1">mg/L</span>
                </p>
              </div>
            )}
            {test.nitrate !== undefined && (
              <div>
                <p className="font-roboto text-xs text-gray-500 mb-1">Nitrate</p>
                <p className="font-roboto text-2xl font-semibold font-mono text-gray-900">
                  {test.nitrate}
                  <span className="text-sm font-normal text-gray-600 ml-1">mg/L</span>
                </p>
              </div>
            )}
            {test.turbidity !== undefined && test.turbidity !== null && (
              <div>
                <p className="font-roboto text-xs text-gray-500 mb-1">Turbidity</p>
                <p className="font-roboto text-2xl font-semibold font-mono text-gray-900">
                  {typeof test.turbidity === 'number' ? test.turbidity.toFixed(1) : test.turbidity}
                  <span className="text-sm font-normal text-gray-600 ml-1">NTU</span>
                </p>
              </div>
            )}
          </div>

          {test.notes && (
            <div className="mt-6 pt-6 border-t border-gray-200/50">
              <p className="font-roboto text-xs text-gray-500 mb-2">Notes</p>
              <p className="font-roboto text-sm text-gray-900 whitespace-pre-wrap">
                {test.notes}
              </p>
            </div>
          )}
        </Card>

        {/* Images Section */}
        {test.imageIds && test.imageIds.length > 0 && (
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-roboto text-lg font-semibold text-gray-900">
                Images ({test.imageIds.length})
              </h3>
              {isImagesLoading && (
                <span className="font-roboto text-sm text-gray-500">Loading...</span>
              )}
            </div>

            {images.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {images.map((image, index) => (
                  <button
                    key={image.imageId}
                    onClick={() => handleImageClick(index)}
                    className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 hover:ring-2 hover:ring-emerald-500 transition-all"
                  >
                    <img
                      src={image.signedUrl}
                      alt={image.fileName}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <ImageIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="font-roboto text-sm">
                  {isImagesLoading ? "Loading images..." : "No images available"}
                </p>
              </div>
            )}

            {signedUrlsResponse?.unauthorized && signedUrlsResponse.unauthorized.length > 0 && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="font-roboto text-sm text-yellow-800">
                  {signedUrlsResponse.unauthorized.length} image(s) could not be loaded due to permissions
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Metadata */}
        <Card>
          <div className="px-6 py-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-sm">
              <div>
                <span className="text-gray-500">Performed By</span>
                <div className="text-gray-700 mt-0.5">
                  {test.performedByName || <span className="text-gray-400 italic">Unknown</span>}
                </div>
              </div>
              <div>
                <span className="text-gray-500">Tested</span>
                <div className="text-gray-700 mt-0.5">{formatDateTime(test.testedAt)}</div>
              </div>
              <div>
                <span className="text-gray-500">Created</span>
                <div className="text-gray-700 mt-0.5">{formatDateTime(test.createdAt)}</div>
              </div>
              <div>
                <span className="text-gray-500">Record ID</span>
                <div className="font-mono text-gray-700 mt-0.5 text-xs break-all">{test.id}</div>
              </div>
              <div>
                <span className="text-gray-500">Location</span>
                <div className="mt-0.5">
                  <LocationDisplay latitude={test.latitude} longitude={test.longitude} />
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Image Gallery Lightbox */}
      {showGallery && images.length > 0 && (
        <ImageGallery
          images={images.map((img) => ({
            imageId: img.imageId,
            fileName: img.fileName,
            signedUrl: img.signedUrl,
          }))}
          initialIndex={galleryStartIndex}
          onClose={() => setShowGallery(false)}
        />
      )}
    </>
  )
}
